/**
 * Pure-function lifecycle policy for voice-message recording.
 *
 * Signal forcibly finalises a voice memo when any of three conditions
 * trip: the user pauses, the recording hits the 3-minute cap, or the
 * mic has been silent for 5+ seconds. The decision must be pure so a
 * tampered UI cannot bypass it — the recorder driver calls into
 * decide() on every metering tick and the answer drives the state
 * machine regardless of what the UI reports.
 *
 * Priorities (so a single decision is unambiguous):
 *
 *   1. userPaused → forceStopUserPaused
 *   2. elapsedMillis ≥ max → forceStopMaxDuration
 *   3. consecutiveSilenceMillis ≥ cap → forceStopSilence
 *   4. otherwise → continueRecording
 *
 * All decisions are pure functions of (elapsedMillis,
 * consecutiveSilenceMillis, userPaused), so the self-test can
 * exhaustively cover every priority pair without touching
 * MediaRecorder or shared mutable state.
 *
 * Pure-logic only: no MediaRecorder, no AudioContext, no side effects.
 * Mirrors `kaether_for_ios/Stoat/E2EE/VoiceMessageRecordingPolicy.swift`.
 */

// ---------------------------------------------------------------------------
// Decision enum
// ---------------------------------------------------------------------------

export type VoiceMessageRecordingDecision =
  | "continueRecording"
  | "forceStopMaxDuration"
  | "forceStopSilence"
  | "forceStopUserPaused";

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export const VoiceMessageRecordingPolicy = {
  maxDurationMillis: 180_000,
  consecutiveSilenceCapMillis: 5_000,

  /**
   * Returns the action the recorder driver must take given the current
   * tick state. Caller is responsible for forwarding the finalisation to
   * the encoder; this function does not touch any audio resource.
   *
   * Priority order (highest first):
   *   1. userPaused → forceStopUserPaused
   *   2. elapsed ≥ max → forceStopMaxDuration
   *   3. consecutiveSilence ≥ cap → forceStopSilence
   *   4. otherwise → continueRecording
   */
  decide(opts: {
    elapsedMillis: number;
    consecutiveSilenceMillis: number;
    userPaused: boolean;
  }): VoiceMessageRecordingDecision {
    if (opts.userPaused) {
      return "forceStopUserPaused";
    }
    if (opts.elapsedMillis >= VoiceMessageRecordingPolicy.maxDurationMillis) {
      return "forceStopMaxDuration";
    }
    if (
      opts.consecutiveSilenceMillis >=
      VoiceMessageRecordingPolicy.consecutiveSilenceCapMillis
    ) {
      return "forceStopSilence";
    }
    return "continueRecording";
  },
} as const;
